import {transparentize} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {buildMarkdownDecoratedCountChildrenTextContentComponent} from '../../../../../srcCommon/formatters/markdown/markdownDecoratedComponents';
import {NodeTransform} from '../../../../../srcCommon/formatters/markdown/markdownFormatterTypes';
import {
  commonMakeMarkdownTransformMentions,
  MentionComponentMap,
} from '../../../../../srcCommon/formatters/markdown/transforms/commonMarkdownTransformMentions';

/*
 * Props.
 */

interface MentionSpanStyleProps {
  $color?: string;
  $isUserMention?: boolean;
}

/*
 * Style.
 */

const StyledMentionSpan = styled.span<MentionSpanStyleProps>`
  color: ${(p) => p.$color || p.theme.interactiveText};
  font-weight: ${fontWeights.semibold};
  ${(p) => maybeAddBackground(p.$isUserMention)}
`;

function maybeAddBackground(isUserMention?: boolean) {
  if (!isUserMention) {
    return '';
  }

  return css`
    background-color: ${(p) => transparentize(0.85, p.theme.palette.blue.shade40)};
    border-radius: 4px;
    padding: 1px 2px 2px;
    margin: 0 -1px;
  `;
}

/*
 * Components.
 */

const NormalMentionWrapper: FC<{children: ReactNode; color: string | undefined}> = (props) => (
  <StyledMentionSpan $color={props.color} $isUserMention={false}>
    {props.children}
  </StyledMentionSpan>
);

const UserMentionWrapper: FC<{children: ReactNode; color: string | undefined}> = (props) => (
  <StyledMentionSpan $color={props.color} $isUserMention>
    {props.children}
  </StyledMentionSpan>
);

const webMentionComponentMap: MentionComponentMap = {
  NormalMentionWrapper: buildMarkdownDecoratedCountChildrenTextContentComponent(NormalMentionWrapper),
  UserMentionWrapper: buildMarkdownDecoratedCountChildrenTextContentComponent(UserMentionWrapper),
};

/*
 * Transform.
 */

export function makeMarkdownTransformMentions(
  mentionableAliases: ReadonlySet<string>,
  color?: string | undefined,
  userAlias?: string | undefined,
): NodeTransform {
  return commonMakeMarkdownTransformMentions({
    mentionableAliases,
    userAlias,
    color,
    components: webMentionComponentMap,
  });
}
